import type { SxProps, Theme } from '@mui/material';

export const loadingContainer: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50vh',
};

export const errorContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '50vh',
  gap: 2,
};

export const headerContainer: SxProps<Theme> = {
  py: 4,
  px: 3,
};

export const headerTopRow: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  mb: 4,
};

export const backButton: SxProps<Theme> = (theme: Theme) => ({
  color: theme.palette.primary.main,
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(101, 31, 255, 0.08)'
        : 'rgba(179, 136, 255, 0.08)',
  },
});

export const idButton: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
});

export const collectionInfoCard: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow:
    theme.palette.mode === 'light'
      ? '0px 2px 2px 2px rgba(0,0,0,0.05)'
      : '0px 2px 4px 0px rgba(0,0,0,0.2)',
  overflow: 'hidden',
  p: 3,
});

export const collectionInfoContainer: SxProps<Theme> = {
  display: 'flex',
  gap: 3,
  alignItems: 'center',
};

export const coverImageContainer: SxProps<Theme> = {
  width: 200,
  height: 200,
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  flexShrink: 0,
};

export const coverImagePlaceholder: SxProps<Theme> = {
  width: 200,
  height: 200,
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: '#f5f5f5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export const collectionDetailsContainer: SxProps<Theme> = {
  flexGrow: 1,
};

export const collectionTitleRow: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  mb: 2,
};

export const playAllButton: SxProps<Theme> = {
  backgroundColor: '#b388ff',
  color: '#130326de',
  width: 56,
  height: 56,
  '&:hover': {
    backgroundColor: '#a070ff',
  },
  '&:disabled': {
    backgroundColor: '#e0e0e0',
    color: '#757575',
  },
};

export const collectionTitle: SxProps<Theme> = {
  fontWeight: 500,
  mb: 1,
};

export const collectionDescription: SxProps<Theme> = {
  lineHeight: 1.5,
};

export const tracksContainer: SxProps<Theme> = {
  px: 3,
  flexGrow: 1,
  pb: 10,
};

export const tableContainer: SxProps<Theme> = {
  backgroundColor: 'transparent',
  boxShadow: 'none',
  border: 'none',
};

export const tableHeaderCell: SxProps<Theme> = {
  color: 'text.secondary',
  fontWeight: 500,
  border: 'none',
  pb: 2,
};

export const tableHeaderInfoContainer: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

export const tableRow: SxProps<Theme> = (theme: Theme) => ({
  cursor: 'pointer',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
  },
  '&:hover .play-button': {
    opacity: 1,
  },
  '&:hover .track-artwork': {
    opacity: 0.6,
  },
});

export const playButton: SxProps<Theme> = {
  opacity: 0,
  position: 'absolute',
  backgroundColor: 'rgba(179, 136, 255, 0.1)',
  color: '#b388ff',
  width: 32,
  height: 32,
  transition: 'all 0.2s ease',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  '&:hover': {
    backgroundColor: '#b388ff',
    color: '#130326de',
  },
};

export const playButtonActive: SxProps<Theme> = {
  opacity: 1,
  position: 'absolute',
  backgroundColor: '#b388ff',
  color: '#130326de',
  width: 32,
  height: 32,
  transition: 'all 0.2s ease',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  '&:hover': {
    backgroundColor: '#b388ff',
    color: '#130326de',
  },
};

export const trackInfoCell: SxProps<Theme> = {
  border: 'none',
  py: 2,
};

export const trackInfoContainer: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};

export const trackArtworkContainer: SxProps<Theme> = {
  width: 48,
  height: 48,
  borderRadius: '4px',
  backgroundColor: '#f5f5f5',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
};

export const trackArtworkImage: SxProps<Theme> = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '4px',
  transition: 'opacity 0.2s ease',
};

export const trackTitle: SxProps<Theme> = {
  fontWeight: 500,
};

export const albumCell: SxProps<Theme> = {
  border: 'none',
  py: 2,
};

export const durationCell: SxProps<Theme> = {
  border: 'none',
  py: 2,
};

export const explicitnessCell: SxProps<Theme> = {
  border: 'none',
  py: 2,
};

export const copyIdsCell: SxProps<Theme> = {
  border: 'none',
  py: 2,
  whiteSpace: 'nowrap',
};

export const moreInfoCell: SxProps<Theme> = {
  border: 'none',
  py: 2,
};

export const moreInfoContainer: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
};

export const newChip: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.action.selected,
  color: theme.palette.text.primary,
  border: 'none',
  borderRadius: '100px',
  fontSize: '13px',
  fontWeight: 400,
  letterSpacing: '0.16px',
  lineHeight: '18px',
  height: '24px',
  padding: '3px 6px',
  fontFamily: '"Mulish", Helvetica, Arial, sans-serif',
});

export const playAllIcon: SxProps<Theme> = {
  fontSize: 28,
};

export const playButtonIcon: SxProps<Theme> = {
  fontSize: 16,
};

export const playFab: SxProps<Theme> = (theme: Theme) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  width: 48,
  height: 48,
  boxShadow:
    '0px 1px 18px 0px rgba(16,30,36,0.12), 0px 6px 10px 0px rgba(16,30,36,0.14), 0px 3px 5px -1px rgba(16,30,36,0.2)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
});

export const playFabIcon: SxProps<Theme> = {
  fontSize: 24,
};
